import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send as SendIcon, AttachFile as PaperclipIcon } from '@mui/icons-material';
import FileUploadArea from './FileUploadArea';
import RoutineCollection from './RoutineCollection';
import StudyPlanTemplate from './StudyPlanTemplate';
import studyPlannerService from '../services/studyPlannerService';
import pdfExportService from '../services/pdfExportService';

const StudyPlannerChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
              content: "Hello! I'm your Study Planning Assistant. I'm here to help you create a personalized study schedule. Let's start by uploading your exam schedule or telling me about your upcoming exams.\n\n📎 You can attach a photo or screenshot of the exam datesheet/planner you received from school, or simply type the exam dates and subjects in free format.",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showRoutineCollection, setShowRoutineCollection] = useState(false);
  const [showStudyPlan, setShowStudyPlan] = useState(false);
  const [userRoutine, setUserRoutine] = useState(null);
  const [studyPlan, setStudyPlan] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Show timeline if a file was attached or if this is the first message after file upload
    if (selectedFile || messages.some(msg => msg.isFile)) {
      console.log('Sending message with file, showing timeline');
      setShowTimeline(true);
    }

    try {
      // Check if message is academic-related
      if (!studyPlannerService.isAcademicMessage(inputMessage)) {
        const redirectMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: "I'm a specialized Study Planning Assistant. I can only help you with academic planning, study schedules, and exam preparation. Please ask me about your studies, exams, or how to create a study plan.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, redirectMessage]);
        setIsTyping(false);
        return;
      }

      // Check if user wants to create a study plan
      if (inputMessage.toLowerCase().includes('create') || inputMessage.toLowerCase().includes('plan') || inputMessage.toLowerCase().includes('schedule')) {
        const aiResponse = {
          id: Date.now() + 1,
          type: 'ai',
          content: "Great! To create your personalized study plan, I need to understand your daily routine. Let me collect some information about your schedule, study preferences, and time management.\n\nClick the button below to start the routine collection process.",
          timestamp: new Date(),
          showRoutineButton: true
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
        return;
      }

      // Send message to AI
      const response = await studyPlannerService.chat(inputMessage);
      
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Sorry, I encountered an error: ${studyPlannerService.formatErrorMessage(error)}. Please try again.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setShowFileUpload(false);
    
    // Add file upload message to chat
    const fileMessage = {
      id: Date.now(),
      type: 'user',
      content: `📎 Uploaded: ${file.name}`,
      timestamp: new Date(),
      isFile: true,
      file: file
    };
    
    setMessages(prev => [...prev, fileMessage]);
    
    // Show timeline when file is uploaded
    console.log('File uploaded, showing timeline');
    setShowTimeline(true);
    
    // Process file with AI
    setIsTyping(true);
    try {
      const response = await studyPlannerService.analyzeDocument(file);
      
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Great! I've analyzed your exam schedule (${file.name}). Here's what I found:\n\n${response.analysis}\n\nPlease review this information and let me know if it's correct. Then I can help you create a personalized study plan!`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error analyzing document:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Sorry, I couldn't analyze your document: ${studyPlannerService.formatErrorMessage(error)}. Please try uploading a different file or type your exam schedule manually.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const handleFileUploadClick = () => {
    setShowFileUpload(!showFileUpload);
  };

  const handleRoutineComplete = async (routine) => {
    setUserRoutine(routine);
    setShowRoutineCollection(false);
    
    // Add routine completion message
    const routineMessage = {
      id: Date.now(),
      type: 'user',
      content: '✅ Routine collected successfully!',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, routineMessage]);
    
    // Generate study plan
    setIsTyping(true);
    try {
      const response = await studyPlannerService.createStudyPlan(
        { examInfo: 'Based on uploaded schedule' },
        routine
      );
      
      const generatedPlan = {
        duration: '4 weeks',
        totalHours: '120h',
        dailyHours: '4h',
        subjects: routine.subjects,
        startDate: new Date().toLocaleDateString(),
        endDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        strategy: [
          'Focus on difficult subjects during peak concentration hours',
          'Use spaced repetition for better retention',
          'Take regular breaks to maintain focus',
          'Review previous day\'s material before starting new topics'
        ],
        schedule: generateSampleSchedule(routine),
        goals: generateSampleGoals(),
        milestones: generateSampleMilestones(),
        progress: { overall: 0, daily: 0 }
      };
      
      setStudyPlan(generatedPlan);
      
      // Save study plan to localStorage for Calendar access
      localStorage.setItem('studyPlan', JSON.stringify(generatedPlan));
      
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Perfect! I've created your personalized study plan based on your routine. Here's what I've prepared for you:\n\n• Study Period: ${generatedPlan.duration}\n• Daily Study Hours: ${generatedPlan.dailyHours}\n• Subjects: ${routine.subjects.length} subjects\n• Strategy: ${generatedPlan.strategy.length} key strategies\n\nWould you like to view your complete study plan?`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error creating study plan:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Sorry, I couldn't create your study plan: ${studyPlannerService.formatErrorMessage(error)}. Please try again.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const generateSampleSchedule = (routine) => {
    const schedule = [];
    const timeSlots = [
      { time: '06:00-07:00', activity: 'Morning Review' },
      { time: '16:00-17:00', activity: 'After School Study' },
      { time: '19:00-20:00', activity: 'Evening Study' },
      { time: '20:00-21:00', activity: 'Revision' }
    ];
    
    timeSlots.forEach(slot => {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const subjectIndex = i % routine.subjects.length;
        days.push({
          subject: routine.subjects[subjectIndex],
          activity: slot.activity,
          color: `bg-${['blue', 'green', 'purple', 'red', 'yellow'][subjectIndex % 5]}-500`
        });
      }
      schedule.push({ time: slot.time, days });
    });
    
    return schedule;
  };

  const generateSampleGoals = () => {
    return [
      {
        title: 'Complete Chapter 1-3',
        description: 'Finish first three chapters of Mathematics',
        deadline: 'Week 1',
        completed: false
      },
      {
        title: 'Practice Problems',
        description: 'Solve 50 practice problems',
        deadline: 'Week 2',
        completed: false
      },
      {
        title: 'Mock Test',
        description: 'Take a full mock test',
        deadline: 'Week 3',
        completed: false
      }
    ];
  };

  const generateSampleMilestones = () => {
    return [
      {
        title: 'Week 1 Complete',
        date: 'End of Week 1',
        completed: false
      },
      {
        title: 'Mid-term Review',
        date: 'Week 2',
        completed: false
      },
      {
        title: 'Final Preparation',
        date: 'Week 4',
        completed: false
      }
    ];
  };

  const handleDownloadPDF = async () => {
    try {
      await pdfExportService.exportStudyPlan(studyPlan, userRoutine);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleShare = () => {
    // Implement sharing functionality
    console.log('Sharing study plan...');
  };

  const handleEdit = () => {
    setShowStudyPlan(false);
    setShowRoutineCollection(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Show routine collection if needed
  if (showRoutineCollection) {
    return (
      <RoutineCollection
        onComplete={handleRoutineComplete}
        onBack={() => setShowRoutineCollection(false)}
      />
    );
  }

  // Show study plan if available
  if (showStudyPlan && studyPlan) {
    return (
      <StudyPlanTemplate
        studyPlan={studyPlan}
        onEdit={handleEdit}
        onDownload={handleDownloadPDF}
        onShare={handleShare}
      />
    );
  }

  return (
    <div className="flex h-[600px] bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Chat Section - 70% */}
      <div className="flex flex-col w-[70%] border-r border-gray-200 dark:border-gray-700">
        {/* Chat Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm">
              🎓
            </div>
            <div>
              <h3 className="text-lg font-bold">AI Study Assistant</h3>
              <p className="text-xs opacity-75">AI-powered study schedule creator</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs opacity-90">Online</span>
          </div>
        </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-5 py-4 ${
                  message.type === 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  {message.type === 'ai' && (
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1">
                      AI
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {message.isFile ? (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800/50 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 dark:text-blue-400 text-lg">📎</span>
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
                              {message.content}
                            </div>
                            <div className="text-xs text-blue-600 dark:text-blue-400">
                              File size: {(message.file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm leading-relaxed whitespace-pre-line text-gray-800 dark:text-gray-200">
                          {message.content}
                        </p>
                      </div>
                    )}
                    {message.showRoutineButton && (
                      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <button
                          onClick={() => setShowRoutineCollection(true)}
                          className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105 font-semibold text-sm"
                        >
                          📋 Start Routine Collection
                        </button>
                      </div>
                    )}
                    <p className={`text-xs mt-2 ${
                      message.type === 'user' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl px-5 py-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  AI
                </div>
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* File Upload Area */}
      <AnimatePresence>
        {showFileUpload && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
          >
            <FileUploadArea
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              selectedFile={selectedFile}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input Area */}
      <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-end space-x-4">
          <div className="flex-1 relative">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              className="w-full p-4 pr-14 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              rows="1"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
            <button
              onClick={handleFileUploadClick}
              className={`absolute right-2 bottom-2 p-1 transition-colors duration-200 ${
                showFileUpload 
                  ? 'text-blue-500 dark:text-blue-400' 
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
              title="Attach file"
            >
              <PaperclipIcon className="w-5 h-5" />
            </button>
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim()}
            className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95"
          >
            <SendIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
      </div>

      {/* Preview Section - 30% */}
      <div className="flex flex-col w-[30%] bg-gray-50 dark:bg-gray-900">
        {/* Preview Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-500 to-blue-600 text-white">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm">
              📋
            </div>
            <div>
              <h3 className="text-lg font-bold">Preview</h3>
              <p className="text-xs opacity-75">Study schedule preview</p>
            </div>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Initial State - No Timeline */}
            {!showTimeline && (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📅</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Ready to Plan Your Exams?
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Upload your exam schedule or type your exam dates to see your personalized timeline here.
                  </p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>📎</span>
                  <span>Click attachment to upload</span>
                </div>
                <button 
                  onClick={() => setShowTimeline(true)}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
                >
                  Test: Show Timeline
                </button>
              </div>
            )}

            {/* Timeline State */}
            {showTimeline && (
              <>
                {/* Timeline Header */}
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">📅 Exam Timeline</h4>
                </div>
                
                {/* Timeline */}
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-800"></div>
                  
                  {/* Timeline Events */}
                  <div className="space-y-6">
                    {/* Event 1 */}
                    <div className="relative flex items-start">
                      <div className="absolute left-2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"></div>
                      <div className="ml-8 flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Dec 15</span>
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Mathematics</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Final Exam - 3 hours</p>
                        </div>
                      </div>
                    </div>

                    {/* Event 2 */}
                    <div className="relative flex items-start">
                      <div className="absolute left-2 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"></div>
                      <div className="ml-8 flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">Dec 18</span>
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Physics</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Theory + Practical</p>
                        </div>
                      </div>
                    </div>

                    {/* Event 3 */}
                    <div className="relative flex items-start">
                      <div className="absolute left-2 w-4 h-4 bg-purple-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"></div>
                      <div className="ml-8 flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">Dec 20</span>
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">English Literature</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Essay Writing</p>
                        </div>
                      </div>
                    </div>

                    {/* Event 4 */}
                    <div className="relative flex items-start">
                      <div className="absolute left-2 w-4 h-4 bg-orange-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"></div>
                      <div className="ml-8 flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">Dec 22</span>
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Chemistry</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Lab + Theory</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPlannerChat; 